/**
 * People.
 *
 * Directive §12 preserves: search across name, phone, case ID and address;
 * the All / Follow-up / No visit scheduled filters; and the person row shape
 * from screenshot 02.
 *
 * Search is sent to the server, which resolves it against blind indexes — the
 * client never holds a searchable plaintext corpus, and no filtering happens
 * here. §12
 */

import { useEffect, useRef, useState } from 'react';
import { ArrowUpTrayIcon, PhoneIcon } from '@heroicons/react/24/outline';

export type PeopleFilter = 'all' | 'follow_up' | 'no_visit' | 'do_not_visit';

export interface PersonSummary {
  id: string;
  name: string;
  caseId: string;
  /** Pre-composed by the server: street, unit and city, already truncated. */
  addressLine: string;
  /** "Done", "Sep 12", "No visit" — the server owns this vocabulary. */
  status: string;
  statusKind: 'done' | 'scheduled' | 'none' | 'today' | 'callback' | 'do_not_visit';
}

const FILTERS: Array<{ id: PeopleFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'follow_up', label: 'Follow-up' },
  { id: 'no_visit', label: 'No visit scheduled' },
  { id: 'do_not_visit', label: 'Do not visit' },
];

export function PeopleScreen({
  people, total, filter, loading, loadingMore, onFilterChange, onSearch,
  onLoadMore, onSelect, canCall = false, onCall,
  canImport = false, onImport,
}: {
  people: PersonSummary[];
  total: number | null;
  filter: PeopleFilter;
  loading: boolean;
  loadingMore: boolean;
  onFilterChange: (f: PeopleFilter) => void;
  onSearch: (q: string, filter: PeopleFilter) => void;
  onLoadMore: (q: string, filter: PeopleFilter) => void;
  onSelect: (id: string) => void;
  canCall?: boolean;
  onCall?: (id: string) => Promise<void>;
  canImport?: boolean;
  onImport?: () => void;
}) {
  const [query, setQuery] = useState('');
  const [callingPersonId, setCallingPersonId] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchHandler = useRef(onSearch);

  useEffect(() => {
    searchHandler.current = onSearch;
  }, [onSearch]);

  // Debounced so a search request is not issued per keystroke. Each request
  // costs a blind-index lookup server-side. §12
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => searchHandler.current(query, filter), 250);
    return () => clearTimeout(debounce.current);
  }, [query, filter]);

  async function openCall(personId: string) {
    if (!onCall || callingPersonId) return;
    setCallingPersonId(personId);
    try { await onCall(personId); }
    finally { setCallingPersonId(null); }
  }

  return (
    <div className="people-screen">
      <div className="people-heading">
        <h1 className="people-title">People</h1>
        {canImport && onImport && (
          <button type="button" className="btn btn-primary people-import" onClick={onImport}>
            <ArrowUpTrayIcon className="h-5 w-5" aria-hidden />
            Import data
          </button>
        )}
      </div>

      <label className="people-search">
        <span className="sr-only">Search people</span>
        <input
          type="search"
          className="input"
          placeholder="Name, phone, case ID or address"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          // Never offer to store a case ID or address in the browser. §81
          data-lpignore="true"
        />
      </label>

      <div role="tablist" aria-label="Filter people" className="people-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            aria-controls="people-results"
            onClick={() => onFilterChange(f.id)}
            className={`people-filter ${filter === f.id ? 'people-filter-active' : ''}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <p className="people-count" aria-live="polite">
        {total === null
          ? loading ? 'Loading people…' : ''
          : `${total} ${total === 1 ? 'person' : 'people'}`}
        {loading && total !== null && <span className="sr-only">, updating results</span>}
      </p>

      {!loading && people.length === 0 && total !== null && (
        <p className="people-empty">
          {query || filter !== 'all'
            ? 'No one matches that search.'
            : 'No People records yet. Use Import data to upload an Excel or CSV file.'}
        </p>
      )}

      <ul id="people-results" className="people-list" aria-busy={loading || loadingMore}>
        {people.map((p) => (
          <li key={p.id} className={`people-row-shell ${p.statusKind === 'do_not_visit' ? 'people-row-excluded' : ''}`}>
            <button
              type="button"
              onClick={() => onSelect(p.id)}
              className="people-row people-row-main"
            >
              <span className="people-identity">
                <span className="people-name">
                  {p.name}
                </span>
                <span className="people-detail">
                  {p.caseId} · {p.addressLine}
                </span>
              </span>
              <StatusTag status={p.status} kind={p.statusKind} />
            </button>
            {canCall && onCall && (
              <button
                type="button"
                className="people-call"
                aria-label={`Call ${p.name}`}
                disabled={callingPersonId === p.id}
                onClick={() => void openCall(p.id)}
              >
                <PhoneIcon className="h-4 w-4" aria-hidden />
                {callingPersonId === p.id ? 'Opening' : 'Call'}
              </button>
            )}
          </li>
        ))}
      </ul>

      {total !== null && people.length < total && (
        <button
          type="button"
          className="people-load-more"
          disabled={loading || loadingMore}
          onClick={() => onLoadMore(query, filter)}
        >
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

/**
 * A person with no visit scheduled is an exception the operator needs to act
 * on, so it takes the magenta tag. Done and scheduled are ordinary states and
 * stay neutral — §7 keeps magenta rare.
 */
function StatusTag({ status, kind }: { status: string; kind: PersonSummary['statusKind'] }) {
  const variant = kind === 'callback' || kind === 'none'
    ? 'people-status-alert'
    : kind === 'do_not_visit'
      ? 'people-status-excluded'
    : kind === 'today'
      ? 'people-status-today'
      : 'people-status-neutral';
  return (
    <span className={`people-status ${variant}`}>
      {status}
    </span>
  );
}
